import { useState } from 'react'
import type { SearchFilters } from '../api/types'
import {
  SHAPES_CORE, SHAPES_EXTENDED, COLORS_CORE, COLORS_EXTENDED, FANCY_HUES, FANCY_INTENSITIES,
  CLARITIES_CORE, CLARITIES_EXTENDED, GRADES, FLUO_ORDER, GROWTH_ORDER, LAB_FALLBACK,
  CARAT_PRESETS_CORE, CARAT_PRESETS_EXTENDED, caratPresetActive, toggleCaratPreset,
  finishShortcutActive, toggleFinishShortcut,
  type FinishShortcutKey, type SupplierFacet,
} from '../lib/filterHelpers'

interface Props {
  value: SearchFilters
  onChange: (next: SearchFilters) => void
  onReset: () => void
  resultCount?: number
  /** When provided, only chips for the value that exist in inventory render.
   *  Pass null while loading to keep the full list visible. */
  availableColors?: Set<string> | null
  availableFancy?: Set<string> | null
  availableIntensities?: Set<string> | null
  availableShapes?: Set<string> | null
  availableClarities?: Set<string> | null
  // Tier-1 grade facets (see feed-filter audit) — chips render only for values
  // that exist in inventory. Null while loading keeps the canonical list visible.
  availablePolish?: Set<string> | null
  availableSymmetry?: Set<string> | null
  availableFluorescence?: Set<string> | null
  availableLab?: Set<string> | null
  availableGrowth?: Set<string> | null
  availableCountries?: Set<string> | null
  /** KYC-gated supplier facet (invariant #3) — the server only sends it to
   *  revealed callers, so this stays null (section hidden) for anon/un-KYC'd. */
  suppliers?: SupplierFacet[] | null
  /** When true, drop the outer card wrapper (border/padding/shadow) so the
   *  sidebar can be embedded inside the mobile filter drawer. Default (false)
   *  keeps the standalone rail rendering identical. */
  bare?: boolean
}

/**
 * Web-only filter sidebar. Mobile has its own FilterSheet flow; keep both
 * surfaces in sync via the same SearchFilters type + lib/filterHelpers.
 *
 * VDB-parity layout (2026-08-03): every section keeps a compact high-signal
 * core visible and folds its long tail behind a per-section "See more" —
 * full D–Z color, FL–I3 clarity, the extended shape taxonomy, carat preset
 * bands, $/ct pricing, location/supplier exclusion — without growing the
 * default rail height.
 */
export function FilterSidebar({ value, onChange, onReset, resultCount, availableColors, availableFancy, availableIntensities, availableShapes, availableClarities, availablePolish, availableSymmetry, availableFluorescence, availableLab, availableGrowth, availableCountries, suppliers, bare = false }: Props) {
  // Per-section "See more" disclosure — collapsed by default, session-local.
  const [more, setMore] = useState<Record<string, boolean>>({})
  const isOpen = (k: string) => !!more[k]
  const toggleMore = (k: string) => setMore((m) => ({ ...m, [k]: !m[k] }))
  // Color sub-tab: fancy auto-selected when a fancy filter is already active
  // (e.g. arriving via deep-link).
  const [colorTab, setColorTab] = useState<'white' | 'fancy'>(() =>
    (value.fancyHues?.length || value.fancyIntensities?.length || value.colorLike) ? 'fancy' : 'white')
  // Price basis: Total is the default; $/ct auto-selected when a ppc bound is set.
  const [priceMode, setPriceMode] = useState<'total' | 'ppc'>(() =>
    (typeof value.ppcMin === 'number' || typeof value.ppcMax === 'number') ? 'ppc' : 'total')

  function toggle<K extends 'shape' | 'clarity' | 'cut' | 'polish' | 'symmetry' | 'fluorescence' | 'lab' | 'stoneType'>(key: K, item: string) {
    const current = (value[key] as string[] | undefined) || []
    const next = current.includes(item)
      ? current.filter((v) => v !== item)
      : [...current, item]
    onChange({ ...value, [key]: next.length ? next : undefined })
  }
  // Intersect a canonical ordering with the values that actually exist in
  // inventory (facet-driven). Null/undefined avail = still loading → show all.
  const gate = (canonical: string[], avail?: Set<string> | null) =>
    avail == null ? canonical : canonical.filter((v) => avail.has(v))
  // White grades (exact `color[]`) and fancy hues/intensities both target the
  // Color column and are AND-ed server-side — sending both yields zero rows (a
  // stone is either white-graded OR a fancy). So the two selections are
  // mutually exclusive: picking one clears the other.
  function toggleColor(item: string) {
    const current = value.color || []
    const next = current.includes(item)
      ? current.filter((v) => v !== item)
      : [...current, item]
    onChange({ ...value, color: next.length ? next : undefined, colorLike: undefined, fancyHues: undefined, fancyIntensities: undefined })
  }
  // Fancy hues are multi-select (fancyHues[], OR server-side). A legacy
  // single-hue colorLike (old shared link) migrates into the array on the
  // first interaction.
  function toggleFancyHue(hue: string) {
    const current = value.fancyHues || (value.colorLike ? [value.colorLike] : [])
    const next = current.includes(hue) ? current.filter((v) => v !== hue) : [...current, hue]
    onChange({ ...value, fancyHues: next.length ? next : undefined, colorLike: undefined, color: undefined })
  }
  function toggleIntensity(grade: string) {
    const current = value.fancyIntensities || []
    const next = current.includes(grade) ? current.filter((v) => v !== grade) : [...current, grade]
    onChange({ ...value, fancyIntensities: next.length ? next : undefined, color: undefined })
  }
  // Location keeps its own toggle so clearing the last country also drops the
  // exclude flag (an exclusion over nothing is meaningless in the URL).
  function toggleCountry(item: string) {
    const current = value.country || []
    const next = current.includes(item)
      ? current.filter((v) => v !== item)
      : [...current, item]
    onChange({ ...value, country: next.length ? next : undefined, countryExclude: next.length ? value.countryExclude : undefined })
  }
  function toggleSupplier(id: number) {
    const current = value.supplierIds || []
    const next = current.includes(id) ? current.filter((v) => v !== id) : [...current, id]
    onChange({ ...value, supplierIds: next.length ? next : undefined, supplierExclude: next.length ? value.supplierExclude : undefined })
  }

  const whiteCore = gate(COLORS_CORE, availableColors)
  const whiteExt = gate(COLORS_EXTENDED, availableColors)
  const fancyList = availableFancy == null ? [] : FANCY_HUES.filter((c) => availableFancy.has(c))
  const intensityList = availableIntensities == null ? [] : FANCY_INTENSITIES.filter((g) => availableIntensities.has(g))
  const shapeCore = gate(SHAPES_CORE, availableShapes)
  const shapeExt = SHAPES_EXTENDED // Tej 2026-08-03: ALL trade shapes always visible, zero-count included
  const clarityCore = gate(CLARITIES_CORE, availableClarities?.size ? availableClarities : null)
  const clarityExt = availableClarities == null ? CLARITIES_EXTENDED : CLARITIES_EXTENDED.filter((c) => availableClarities.has(c))
  const polishList = gate(GRADES, availablePolish)
  const symmetryList = gate(GRADES, availableSymmetry)
  const fluoList = gate(FLUO_ORDER, availableFluorescence)
  // Growth Type: CVD/HPHT are facet-gated, but Natural is ALWAYS shown (Tej
  // 2026-07-27) — buyers expect to see it as an option. The catalog has no
  // naturals today, so picking it simply returns no matches ("displays none").
  const growthList = (() => {
    const gated = gate(GROWTH_ORDER, availableGrowth)
    return gated.includes('Natural') ? gated : [...gated, 'Natural']
  })()
  // Labs: inventory facets when loaded (sorted), canonical fallback while
  // loading. "None" (uncertified) is ALWAYS offered — same principle as
  // Natural above: a real buyer preference the catalog may not carry today.
  const labList = (() => {
    const base = availableLab == null ? LAB_FALLBACK : [...availableLab].sort()
    return base.includes('None') ? base : [...base, 'None']
  })()
  // Countries = stone origins present in inventory (facet-driven), sorted. The
  // precomputed search artifact predates the country facet, so fall back to the
  // known live-catalog origins until the rebuilt artifact carries real counts.
  const countryList = (availableCountries && availableCountries.size > 0)
    ? [...availableCountries].sort()
    : ['India', 'United States']
  const supplierList = suppliers || []

  const activeCount =
    (value.shape?.length ?? 0)
    + (value.color?.length ?? 0)
    + (value.clarity?.length ?? 0)
    + (value.cut?.length ?? 0)
    + (value.polish?.length ?? 0)
    + (value.symmetry?.length ?? 0)
    + (value.fluorescence?.length ?? 0)
    + (value.lab?.length ?? 0)
    + (value.stoneType?.length ?? 0)
    + (value.country?.length ?? 0)
    + (value.colorLike ? 1 : 0)
    + (value.fancyHues?.length ?? 0)
    + (value.fancyIntensities?.length ?? 0)
    + (typeof value.caratMin === 'number' ? 1 : 0)
    + (typeof value.caratMax === 'number' ? 1 : 0)
    + (typeof value.priceMin === 'number' ? 1 : 0)
    + (typeof value.priceMax === 'number' ? 1 : 0)
    + (typeof value.ppcMin === 'number' || typeof value.ppcMax === 'number' ? 1 : 0)
    + (typeof value.ratioMin === 'number' || typeof value.ratioMax === 'number' ? 1 : 0)
    + (typeof value.lengthMin === 'number' || typeof value.lengthMax === 'number' ? 1 : 0)
    + (value.stockIds?.length ? 1 : 0)
    + (typeof value.supplierId === 'number' ? 1 : 0)
    + (value.supplierIds?.length ?? 0)
    + (value.heartsArrows ? 1 : 0)
    + (value.eyeClean ? 1 : 0)
    + (value.noBGM ? 1 : 0)
  const hasActive = activeCount > 0

  return (
    <aside className={bare ? 'text-sm' : 'panel rounded-lg p-5 text-sm'}>
      {/* In `bare` (mobile drawer) mode the drawer supplies its own
          "Filters" header, so we drop this title to avoid a duplicate and
          keep only the Clear affordance (right-aligned). */}
      {/* v2: the panel title sits on the THICK rule — the sidebar's one
          hierarchy anchor, matching the section header + table underline.
          Every group divider below stays hairline. */}
      <div className={`flex items-center pb-4 mb-1 ${bare ? 'justify-end border-b border-line' : 'justify-between border-b-2 border-ink'}`}>
        {!bare && (
          <p className="text-lg font-semibold text-ink tracking-h3">
            Filters
            {hasActive && (
              <span className="ml-2 inline-flex items-center justify-center rounded-full bg-ink text-paper text-[10px] font-bold h-4 min-w-[16px] px-1 align-middle tabular">
                {activeCount}
              </span>
            )}
          </p>
        )}
        <button
          type="button"
          onClick={onReset}
          disabled={!hasActive}
          className={
            hasActive
              ? 'inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-navy-500 hover:text-gold-600 transition-colors'
              : 'text-[11px] font-medium uppercase tracking-[0.1em] text-navy-300 cursor-not-allowed'
          }
          data-testid="filter-clear"
        >
          {hasActive && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          )}
          Clear
        </button>
      </div>
      {typeof resultCount === 'number' && (
        <p className="font-mono text-xs text-soft tabular mb-5">
          {resultCount === 0 ? 'No matches' : `${resultCount.toLocaleString()} stone${resultCount === 1 ? '' : 's'}`}
        </p>
      )}

      <div className="space-y-6">
        {(shapeCore.length > 0 || shapeExt.length > 0) && (
          <Group title="Shape">
            <div className="grid grid-cols-4 gap-2">
              {shapeCore.map((s) => (
                <ShapeTile
                  key={s}
                  label={s}
                  active={(value.shape || []).includes(s)}
                  onClick={() => toggle('shape', s)}
                />
              ))}
              {isOpen('shape') && shapeExt.map((s) => (
                <ShapeTile
                  key={s}
                  label={s}
                  active={(value.shape || []).includes(s)}
                  onClick={() => toggle('shape', s)}
                />
              ))}
            </div>
            {shapeExt.length > 0 && (
              <SeeMore open={isOpen('shape')} onToggle={() => toggleMore('shape')} moreCount={shapeExt.length} />
            )}
          </Group>
        )}

        {/* One Color group with White | Fancy sub-tabs (they're mutually
            exclusive filters over the same column). The Fancy tab only exists
            when inventory actually carries fancy stones (facet-driven). */}
        <Group title="Color">
          {fancyList.length > 0 && (
            <SegTabs
              options={[{ key: 'white', label: 'White' }, { key: 'fancy', label: 'Fancy' }]}
              active={colorTab}
              onPick={(k) => setColorTab(k as 'white' | 'fancy')}
            />
          )}
          {colorTab === 'white' || fancyList.length === 0 ? (
            <>
              <div className="flex flex-wrap gap-1.5">
                {whiteCore.map((c) => (
                  <Chip key={c} label={c} active={(value.color || []).includes(c)} onClick={() => toggleColor(c)} />
                ))}
                {isOpen('color') && whiteExt.map((c) => (
                  <Chip key={c} label={c} active={(value.color || []).includes(c)} onClick={() => toggleColor(c)} />
                ))}
              </div>
              {whiteExt.length > 0 && (
                <SeeMore open={isOpen('color')} onToggle={() => toggleMore('color')} moreCount={whiteExt.length} />
              )}
              {/* No BGM (VDB) — strict opt-in to stones a feed explicitly
                  certifies free of brown/green/milky tint. Dormant until a
                  feed maps the flag (same pattern as Hearts & Arrows #53). */}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Chip
                  label="No BGM"
                  active={value.noBGM === true}
                  onClick={() => onChange({ ...value, noBGM: value.noBGM ? undefined : true })}
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-wrap gap-1.5">
                {fancyList.map((c) => (
                  <Chip
                    key={`fancy-${c}`}
                    label={c}
                    active={(value.fancyHues || []).includes(c) || value.colorLike === c}
                    onClick={() => toggleFancyHue(c)}
                    fancy
                  />
                ))}
              </div>
              {intensityList.length > 0 && (
                <>
                  <p className="mt-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-soft">Intensity</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {intensityList.map((g) => (
                      <Chip key={g} label={g.replace(/^Fancy ?/, '') || 'Fancy'} active={(value.fancyIntensities || []).includes(g)} onClick={() => toggleIntensity(g)} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </Group>

        {clarityCore.length > 0 && (
          <Group title="Clarity">
            <div className="flex flex-wrap gap-1.5">
              {clarityCore.map((c) => (
                <Chip key={c} label={c} active={(value.clarity || []).includes(c)} onClick={() => toggle('clarity', c)} />
              ))}
              {isOpen('clarity') && clarityExt.map((c) => (
                <Chip key={c} label={c} active={(value.clarity || []).includes(c)} onClick={() => toggle('clarity', c)} />
              ))}
            </div>
            {clarityExt.length > 0 && (
              <SeeMore open={isOpen('clarity')} onToggle={() => toggleMore('clarity')} moreCount={clarityExt.length} />
            )}
            {/* Eye Clean (VDB) — dormant strict opt-in like No BGM above. */}
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Chip
                label="Eye Clean"
                active={value.eyeClean === true}
                onClick={() => onChange({ ...value, eyeClean: value.eyeClean ? undefined : true })}
              />
            </div>
          </Group>
        )}

        {/* Carat before Cut/Polish/Symmetry, per Vikram's VDB ordering (2026-07-26). */}
        <Group title="Carat Weight">
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              step={0.1}
              value={value.caratMin ?? ''}
              placeholder="Min"
              onChange={(e) => onChange({
                ...value,
                caratMin: e.target.value ? Number(e.target.value) : undefined,
              })}
              className="h-10 w-full min-w-0 rounded-sm border border-line bg-paper px-3 text-sm text-ink tabular placeholder:text-soft focus:border-gold focus:outline-none transition-colors"
            />
            <span className="text-soft">–</span>
            <input
              type="number"
              min={0}
              step={0.1}
              value={value.caratMax ?? ''}
              placeholder="Max"
              onChange={(e) => onChange({
                ...value,
                caratMax: e.target.value ? Number(e.target.value) : undefined,
              })}
              className="h-10 w-full min-w-0 rounded-sm border border-line bg-paper px-3 text-sm text-ink tabular placeholder:text-soft focus:border-gold focus:outline-none transition-colors"
            />
            <span className="text-[11px] font-medium uppercase tracking-wide text-soft">ct</span>
          </div>
          {/* VDB-style quick bands — single-select toggle writing the same
              min/max the inputs edit (picking a band again clears it). */}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {CARAT_PRESETS_CORE.map((p) => (
              <Chip key={p.label} label={p.label} active={caratPresetActive(value, p)} onClick={() => onChange(toggleCaratPreset(value, p))} />
            ))}
            {isOpen('carat') && CARAT_PRESETS_EXTENDED.map((p) => (
              <Chip key={p.label} label={p.label} active={caratPresetActive(value, p)} onClick={() => onChange(toggleCaratPreset(value, p))} />
            ))}
          </div>
          <SeeMore open={isOpen('carat')} onToggle={() => toggleMore('carat')} moreCount={CARAT_PRESETS_EXTENDED.length} />
        </Group>

        {/* Cut, Polish & Symmetry in ONE group: the 3EX+/3VG+ shortcuts cover
            what most buyers actually pick; the per-grade rows for Polish and
            Symmetry sit behind See more. (VDB's "8X" is omitted — no feed
            supplies IGI's 8X program flag.) */}
        <Group title="Cut, Polish & Symmetry">
          <div className="flex flex-wrap gap-1.5">
            {(['3EX+', '3VG+'] as FinishShortcutKey[]).map((k) => (
              <Chip key={k} label={k} active={finishShortcutActive(value, k)} onClick={() => onChange(toggleFinishShortcut(value, k))} />
            ))}
            {/* Hearts & Arrows (#53) — always visible (Natural-pattern, Tej):
                strict opt-in filter to feed-flagged H&A stones. No live feed
                supplies the flag yet, so selecting it can zero the results —
                the grid's "Clear all filters" is the escape; it lights up the
                moment a feed maps a heartsArrows column. */}
            <Chip
              label="Hearts & Arrows"
              active={value.heartsArrows === true}
              onClick={() => onChange({
                ...value,
                heartsArrows: value.heartsArrows ? undefined : true,
              })}
            />
          </div>
          <p className="mt-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-soft">Cut</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {GRADES.filter((g) => g !== 'Fair').map((c) => (
              <Chip key={c} label={c} active={(value.cut || []).includes(c)} onClick={() => toggle('cut', c)} />
            ))}
          </div>
          {isOpen('finish') && (
            <>
              {polishList.length > 0 && (
                <>
                  <p className="mt-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-soft">Polish</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {polishList.map((c) => (
                      <Chip key={c} label={c} active={(value.polish || []).includes(c)} onClick={() => toggle('polish', c)} />
                    ))}
                  </div>
                </>
              )}
              {symmetryList.length > 0 && (
                <>
                  <p className="mt-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-soft">Symmetry</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {symmetryList.map((c) => (
                      <Chip key={c} label={c} active={(value.symmetry || []).includes(c)} onClick={() => toggle('symmetry', c)} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
          {(polishList.length > 0 || symmetryList.length > 0) && (
            <SeeMore open={isOpen('finish')} onToggle={() => toggleMore('finish')} labelMore="Polish & symmetry" />
          )}
        </Group>

        {fluoList.length > 0 && (
          <Group title="Fluorescence">
            <div className="flex flex-wrap gap-1.5">
              {fluoList.map((c) => (
                <Chip key={c} label={c} active={(value.fluorescence || []).includes(c)} onClick={() => toggle('fluorescence', c)} />
              ))}
            </div>
          </Group>
        )}

        {labList.length > 0 && (
          <Group title="Lab">
            <div className="flex flex-wrap gap-1.5">
              {labList.map((c) => (
                <Chip key={c} label={c} active={(value.lab || []).includes(c)} onClick={() => toggle('lab', c)} />
              ))}
            </div>
          </Group>
        )}

        {growthList.length > 0 && (
          <Group title="Growth Type">
            <div className="flex flex-wrap gap-1.5">
              {growthList.map((c) => (
                <Chip key={c} label={c} active={(value.stoneType || []).includes(c)} onClick={() => toggle('stoneType', c)} />
              ))}
            </div>
          </Group>
        )}

        {countryList.length > 0 && (
          <Group title="Location">
            <div className="flex flex-wrap gap-1.5">
              {countryList.map((c) => (
                <Chip key={c} label={c} active={(value.country || []).includes(c)} onClick={() => toggleCountry(c)} />
              ))}
            </div>
            {(value.country?.length ?? 0) > 0 && (
              <ExcludeCheck
                checked={value.countryExclude === true}
                label={`Exclude selected location${(value.country?.length ?? 0) === 1 ? '' : 's'}`}
                onToggle={() => onChange({ ...value, countryExclude: value.countryExclude ? undefined : true })}
              />
            )}
          </Group>
        )}

        {/* Supplier — renders ONLY when the server sent the KYC-gated supplier
            facet (signed-in + KYC-approved callers; invariant #3). A type-ahead
            SEARCH, never the full list: the catalog can carry thousands of
            suppliers and listing them all one-by-one is unusable. */}
        {supplierList.length > 0 && (
          <Group title="Supplier">
            <SupplierSearch
              suppliers={supplierList}
              selected={value.supplierIds || []}
              onToggle={toggleSupplier}
            />
            {(value.supplierIds?.length ?? 0) > 0 && (
              <ExcludeCheck
                checked={value.supplierExclude === true}
                label={`Exclude selected supplier${(value.supplierIds?.length ?? 0) === 1 ? '' : 's'}`}
                onToggle={() => onChange({ ...value, supplierExclude: value.supplierExclude ? undefined : true })}
              />
            )}
          </Group>
        )}

        <Group title="Price (USD)">
          {/* Total vs $/ct basis — both ranges can coexist server-side; the
              tabs just switch which pair these inputs edit. */}
          <SegTabs
            options={[{ key: 'total', label: 'Total' }, { key: 'ppc', label: '$ / ct' }]}
            active={priceMode}
            onPick={(k) => setPriceMode(k as 'total' | 'ppc')}
          />
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-soft">$</span>
              <input
                type="number"
                min={0}
                step={100}
                value={(priceMode === 'total' ? value.priceMin : value.ppcMin) ?? ''}
                placeholder="Min"
                onChange={(e) => {
                  const n = e.target.value ? Number(e.target.value) : undefined
                  onChange(priceMode === 'total' ? { ...value, priceMin: n } : { ...value, ppcMin: n })
                }}
                className="h-10 w-full min-w-0 rounded-sm border border-line bg-paper pl-7 pr-3 text-sm text-ink tabular placeholder:text-soft focus:border-gold focus:outline-none transition-colors"
              />
            </div>
            <span className="text-soft">–</span>
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-soft">$</span>
              <input
                type="number"
                min={0}
                step={100}
                value={(priceMode === 'total' ? value.priceMax : value.ppcMax) ?? ''}
                placeholder="Max"
                onChange={(e) => {
                  const n = e.target.value ? Number(e.target.value) : undefined
                  onChange(priceMode === 'total' ? { ...value, priceMax: n } : { ...value, ppcMax: n })
                }}
                className="h-10 w-full min-w-0 rounded-sm border border-line bg-paper pl-7 pr-3 text-sm text-ink tabular placeholder:text-soft focus:border-gold focus:outline-none transition-colors"
              />
            </div>
            {priceMode === 'ppc' && (
              <span className="text-[11px] font-medium uppercase tracking-wide text-soft">/ct</span>
            )}
          </div>
        </Group>

        {/* Ratio + measurements last, per Vikram's VDB ordering (2026-07-26).
            Ratio is computed server-side from L/W; mm ranges are 100% backed. */}
        <Group title="Ratio (L / W)">
          <NumRange
            min={value.ratioMin} max={value.ratioMax} step={0.01} placeholderMin="1.00" placeholderMax="2.50"
            onMin={(n) => onChange({ ...value, ratioMin: n })}
            onMax={(n) => onChange({ ...value, ratioMax: n })}
          />
        </Group>

        <Group title="Measurements (mm)">
          <div className="space-y-2">
            <NumRange
              label="L" min={value.lengthMin} max={value.lengthMax} step={0.1}
              onMin={(n) => onChange({ ...value, lengthMin: n })}
              onMax={(n) => onChange({ ...value, lengthMax: n })}
            />
            <NumRange
              label="W" min={value.widthMin} max={value.widthMax} step={0.1}
              onMin={(n) => onChange({ ...value, widthMin: n })}
              onMax={(n) => onChange({ ...value, widthMax: n })}
            />
          </div>
        </Group>
      </div>
    </aside>
  )
}

/** Per-section progressive disclosure — the one new interaction primitive.
 *  Styled like the sidebar's other text actions (Clear): quiet until hover. */
function SeeMore({ open, onToggle, moreCount, labelMore }: { open: boolean; onToggle: () => void; moreCount?: number; labelMore?: string }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-navy-500 hover:text-gold-600 transition-colors"
    >
      {open ? 'See less' : labelMore ? `${labelMore} +` : `See more${moreCount ? ` (${moreCount})` : ''}`}
      <svg
        width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"
        className={`transition-transform ${open ? 'rotate-180' : ''}`}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  )
}

/** Underline sub-tabs (White|Fancy, Total|$per-ct) — gold accent for active,
 *  matching the app's tab treatment. Mode switches, NOT filter chips. */
function SegTabs({ options, active, onPick }: { options: { key: string; label: string }[]; active: string; onPick: (k: string) => void }) {
  return (
    <div className="mb-2.5 flex items-center gap-4 border-b border-line">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onPick(o.key)}
          className={`-mb-px border-b-2 pb-1.5 text-xs font-semibold transition-colors ${
            active === o.key ? 'border-gold text-ink' : 'border-transparent text-soft hover:text-ink'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** Compact exclude toggle for the Location/Supplier lists (VDB "Exclude
 *  Location(s)"). Only rendered once something is selected to invert. */
function ExcludeCheck({ checked, label, onToggle }: { checked: boolean; label: string; onToggle: () => void }) {
  return (
    <label className="mt-2 flex cursor-pointer select-none items-center gap-2 text-xs text-ink">
      <input type="checkbox" checked={checked} onChange={onToggle} className="h-3.5 w-3.5 accent-gold" />
      {label}
    </label>
  )
}

/** Compact numeric min–max range row, optional leading axis label (L / W). */
function NumRange({
  label, min, max, step = 1, placeholderMin = 'Min', placeholderMax = 'Max', onMin, onMax,
}: {
  label?: string
  min: number | null | undefined
  max: number | null | undefined
  step?: number
  placeholderMin?: string
  placeholderMax?: string
  onMin: (n: number | undefined) => void
  onMax: (n: number | undefined) => void
}) {
  const cls = 'h-10 w-full min-w-0 rounded-sm border border-line bg-paper px-3 text-sm text-ink tabular placeholder:text-soft focus:border-gold focus:outline-none transition-colors'
  return (
    <div className="flex items-center gap-2">
      {label && <span className="w-4 shrink-0 text-[11px] font-semibold uppercase text-soft">{label}</span>}
      <input type="number" min={0} step={step} value={min ?? ''} placeholder={placeholderMin}
        onChange={(e) => onMin(e.target.value ? Number(e.target.value) : undefined)} className={cls} />
      <span className="text-soft">–</span>
      <input type="number" min={0} step={step} value={max ?? ''} placeholder={placeholderMax}
        onChange={(e) => onMax(e.target.value ? Number(e.target.value) : undefined)} className={cls} />
    </div>
  )
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2.5">
      {/* Label style, navy-300 (spec) — NOT gold. Gold stays the accent
          reserved for active pills + focus + the sorted caret. */}
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-navy-300">{title}</p>
      {children}
    </div>
  )
}

function Chip({ label, active, onClick, fancy = false }: { label: string; active: boolean; onClick: () => void; fancy?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1.5 min-w-[2.25rem] h-8 rounded-full px-3 text-xs font-medium transition-all duration-micro active:scale-[0.95] border ${
        active
          ? 'bg-sapphire text-white border-sapphire'
          : 'bg-paper text-ink border-line hover:border-navy-300'
      }`}
    >
      {fancy && (
        <span className={`inline-block h-1.5 w-1.5 rounded-full ${active ? 'bg-gold-300' : 'bg-gold-500'}`} aria-hidden />
      )}
      {label}
    </button>
  )
}

/**
 * Supplier type-ahead — a SEARCH, not a list. The catalog can carry thousands
 * of suppliers; rendering them all one-by-one is unusable, so we show only:
 *   • the currently-selected suppliers (always visible, so a pick survives an
 *     empty search box), and
 *   • up to SUPPLIER_MATCH_CAP name matches once the box has ≥1 char.
 * Filtering is client-side over the KYC-gated facet the server already sent —
 * no new query.
 */
const SUPPLIER_MATCH_CAP = 8
function SupplierSearch({ suppliers, selected, onToggle }: {
  suppliers: SupplierFacet[]
  selected: number[]
  onToggle: (id: number) => void
}) {
  const [q, setQ] = useState('')
  const needle = q.trim().toLowerCase()
  const selectedSet = new Set(selected)
  const selectedList = suppliers.filter((s) => selectedSet.has(s.id))
  // Match on name; drop already-selected (they render in their own row above),
  // then cap so the panel never grows unbounded.
  const matches = needle
    ? suppliers
      .filter((s) => !selectedSet.has(s.id) && s.name.toLowerCase().includes(needle))
      .slice(0, SUPPLIER_MATCH_CAP)
    : []
  return (
    <div className="space-y-2">
      {selectedList.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedList.map((s) => (
            <Chip key={s.id} label={s.name} active onClick={() => onToggle(s.id)} />
          ))}
        </div>
      )}
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${suppliers.length.toLocaleString()} supplier${suppliers.length === 1 ? '' : 's'}…`}
        className="h-10 w-full rounded-sm border border-line bg-paper px-3 text-sm text-ink placeholder:text-soft focus:border-gold focus:outline-none transition-colors"
        aria-label="Search suppliers"
      />
      {needle && (
        matches.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {matches.map((s) => (
              <Chip key={s.id} label={s.name} active={selectedSet.has(s.id)} onClick={() => onToggle(s.id)} />
            ))}
          </div>
        ) : (
          <p className="text-xs text-soft">No supplier matches “{q.trim()}”.</p>
        )
      )}
    </div>
  )
}

/**
 * Shape tile — a small line-icon of the cut above its name, laid out in a
 * grid (matches the "diamond exchange" mockup shape picker). Active tile
 * fills navy; the glyph inverts to white. These are UI icons for the filter,
 * not stone imagery — real photos only appear in the results (StoneImage).
 */
function ShapeTile({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={label}
      className={`group flex flex-col items-center justify-center gap-1 rounded-md border py-2.5 px-1 transition-all duration-micro active:scale-[0.96] ${
        active
          ? 'bg-ink border-ink text-paper'
          : 'bg-paper border-line text-ink hover:border-navy-300'
      }`}
    >
      <ShapeGlyph shape={label} className={active ? 'text-white' : 'text-navy-500 group-hover:text-navy-700'} />
      <span className="text-[10px] font-medium leading-none tracking-tight">{label}</span>
    </button>
  )
}

// Compact stroke-only shape glyphs (viewBox 0 0 100 100) — hairline
// line-icons for the shape filter tiles (UI icons, not stone imagery).
// Keyed by the canonical labels from functions/src/shared/shapes.ts,
// lowercased (ShapeGlyph lowercases before lookup, tiles pass the canonical
// label). Extended (VDB) shapes reuse the closest silhouette where the outline
// is genuinely the same (rose/euro/portuguese = round profile, old mine =
// cushion profile, briolette = pear drop); only true unknowns fall to the
// generic gem profile — never a bare circle. Legacy spellings kept as aliases.
const GLYPH_PATHS: Record<string, string> = {
  round:    'M50 6 a44 44 0 1 0 0.1 0 z',
  oval:     'M50 6 C72 6 84 26 84 50 C84 74 72 94 50 94 C28 94 16 74 16 50 C16 26 28 6 50 6 z',
  pear:     'M50 6 Q80 30 80 60 Q80 92 50 92 Q20 92 20 60 Q20 30 50 6 z',
  marquise: 'M50 6 Q88 50 50 94 Q12 50 50 6 z',
  emerald:  'M24 12 H76 L88 24 V76 L76 88 H24 L12 76 V24 z',
  cushion:  'M28 10 H72 Q90 10 90 28 V72 Q90 90 72 90 H28 Q10 90 10 72 V28 Q10 10 28 10 z',
  princess: 'M12 12 H88 V88 H12 z',
  radiant:  'M24 12 H76 L88 24 V76 L76 88 H24 L12 76 V24 z',
  heart:    'M50 90 C18 68 8 44 22 26 C33 12 47 17 50 30 C53 17 67 12 78 26 C92 44 82 68 50 90 z',
  asscher:  'M24 12 H76 L88 24 V76 L76 88 H24 L12 76 V24 z',
  'baguette':          'M32 10 H68 V90 H32 z',
  'trillion':          'M50 10 L90 82 H10 z',
  'trilliant':         'M50 10 L90 82 H10 z',
  'half moon':         'M15 38 H85 Q85 88 50 88 Q15 88 15 38 z',
  'trapezoid':         'M26 22 H74 L90 78 H10 z',
  'kite':              'M50 6 L82 40 L50 94 L18 40 z',
  'shield':            'M50 8 L86 26 Q86 70 50 92 Q14 70 14 26 z',
  'bullet':            'M35 90 V40 Q35 12 50 8 Q65 12 65 40 V90 z',
  'hexagon':           'M50 8 L86 29 V71 L50 92 L14 71 V29 z',
  'hexagonal':         'M50 8 L86 29 V71 L50 92 L14 71 V29 z',
  'octagon':           'M32 10 H68 L90 32 V68 L68 90 H32 L10 68 V32 z',
  'octagonal':         'M32 10 H68 L90 32 V68 L68 90 H32 L10 68 V32 z',
  'pentagon':          'M50 8 L90 38 L74 88 H26 L10 38 z',
  'pentagonal':        'M50 8 L90 38 L74 88 H26 L10 38 z',
  'lozenge':           'M50 8 L82 50 L50 92 L18 50 z',
  'briolette':         'M50 6 Q80 30 80 60 Q80 92 50 92 Q20 92 20 60 Q20 30 50 6 z',
  // Rose / Old European / Portuguese are round-outline cuts; Old Mine is a
  // cushiony (rounded-square) cut; Carré is a step-cut square.
  'rose':              'M50 6 a44 44 0 1 0 0.1 0 z',
  'old mine':          'M28 10 H72 Q90 10 90 28 V72 Q90 90 72 90 H28 Q10 90 10 72 V28 Q10 10 28 10 z',
  'old european':      'M50 6 a44 44 0 1 0 0.1 0 z',
  'carre':             'M16 16 H84 V84 H16 z',
  'square':            'M16 16 H84 V84 H16 z',
  'portuguese':        'M50 6 a44 44 0 1 0 0.1 0 z',
  'other':             'M30 14 H70 L90 40 L50 90 L10 40 z',
}

function ShapeGlyph({ shape, className }: { shape: string; className?: string }) {
  const d = GLYPH_PATHS[shape.toLowerCase()] ?? GLYPH_PATHS.other
  return (
    <svg
      viewBox="0 0 100 100"
      className={`h-5 w-5 shrink-0 transition-colors ${className || ''}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={6}
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={d} />
    </svg>
  )
}
